import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import {
  Button,
  buttonStyles,
  Card,
  ConfirmModal,
  FieldGrid,
  LoadingOverlay,
  Menu,
  SaveBar,
} from '@skupreme/shared/ui';
import {
  FormCheckbox,
  FormNumberField,
  FormSelect,
  FormPhoneField,
  FormTextField,
} from '@skupreme/shared/ui/form';
import { useEffect, useMemo, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useAuth } from '@contexts/auth';
import { allMarketplaceRegionDropdownHook } from '@hooks/marketplace-region';
import { ErrorMessageConstant } from '@utility/constants/common';
import { StorageProviderHelpText } from '@utility/constants/strings';
import StorageProviderService from '@utility/services/storage-provider';
import { ToastMessage } from '@utility/toast';
import { ERROR_MESSAGES } from '@utility/validations';
import { ExcludedCarriersField } from '../../legacy-bridge';
import {
  PAPER_TYPES,
  STOCK_TIERS,
  cutoffTimeOptions,
  detailsAccess,
  detailsSchema,
  toDetailsForm,
  toDetailsPayload,
  type ProviderDetail,
  type ProviderDetailsValues,
} from '../../lib/provider-details';
import { AddProviderModal } from '../add-provider/AddProviderModal';
import { isZipValid, toCountryOptions, type Region } from '../country-options';
import { CredentialPopup } from './CredentialPopup';
import {
  KindField,
  QuantityTypeField,
  SenderNameField,
  WarehouseCodesField,
} from './DetailsFields';

type ProviderDetailsTabProps = {
  data: ProviderDetail | undefined;
  loading: boolean;
  updateData: (provider: ProviderDetail) => void;
  refresh: () => void;
  isUser: boolean;
  isFBA: boolean;
  isAWD: boolean;
  isManual: boolean;
};

const PAPER_OPTIONS = PAPER_TYPES.map(type => ({ value: type, label: type }));

export function ProviderDetailsTab({
  data,
  loading,
  updateData,
  refresh,
  isUser,
  isFBA,
  isAWD,
  isManual,
}: ProviderDetailsTabProps) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [changeTypeOpen, setChangeTypeOpen] = useState(false);
  const [kindMissing, setKindMissing] = useState(false);
  const [dialCodeLength, setDialCodeLength] = useState(1);
  const [phoneCountry, setPhoneCountry] = useState<string>();

  const { data: regionData, loading: regionLoading } =
    allMarketplaceRegionDropdownHook();
  const regions = regionData as Region[];
  const countryOptions = useMemo(() => toCountryOptions(regions), [regions]);

  const access = detailsAccess(data?.integration_type, { isFBA, isAWD });
  const defaults = useMemo(
    () => (data?._id ? toDetailsForm(data) : undefined),
    [data],
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { dirtyFields, submitCount },
  } = useForm<ProviderDetailsValues>({
    resolver: zodResolver(
      detailsSchema({ requiresName: !isFBA, dialCodeLength }),
    ) as unknown as Resolver<ProviderDetailsValues>,
    defaultValues: defaults,
    /* A field that is not drawn leaves the payload, as it did before. */
    shouldUnregister: true,
  });

  useEffect(() => {
    if (!loading && defaults) {
      reset(defaults);
      setKindMissing(false);
    }
  }, [defaults, loading, reset]);

  /* The phone starts on the provider's country until a number is saved. */
  useEffect(() => {
    if (!data?.contact_phone && data?.address?.country) {
      setPhoneCountry(data.address.country.toLowerCase());
    }
  }, [data]);

  const isFulfillable = watch('is_fulfillable');
  const isWarehouse = watch('is_warehouse');
  const country = watch('country');
  const zip = watch('zip_code');
  const cutoff = watch('order_cutoff_time');
  const useLabelName = watch('use_label_name');

  const zipValid = isZipValid(regions, country, zip);
  const showKindError = kindMissing && !access.allowsNoKind;
  const cutoffOptions = useMemo(() => cutoffTimeOptions(cutoff), [cutoff]);
  const isDirty = Object.keys(dirtyFields).length > 0;

  useEffect(() => {
    if (isFulfillable || isWarehouse) setKindMissing(false);
  }, [isFulfillable, isWarehouse]);

  const save = async (values: ProviderDetailsValues) => {
    if (!data || !zipValid) return;
    if (
      !values.is_fulfillable &&
      !values.is_warehouse &&
      !access.allowsNoKind
    ) {
      setKindMissing(true);
      return;
    }

    try {
      setSaving(true);
      const result = await StorageProviderService.update(
        data._id,
        toDetailsPayload(values, data),
      );
      updateData(result.data);
      ToastMessage.success('Storage Provider updated successfully.');
      refresh();
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!data) return;
    try {
      setDeleting(true);
      await StorageProviderService.remove(data._id);
      ToastMessage.success('Storage Provider deleted successfully.');
      navigate('/app/scm/storage-provider');
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setDeleting(false);
    }
  };

  const onCountrySelect = (regionId: string) => {
    if (data?.contact_phone) return;
    const region = regions.find(entry => entry.id === regionId);
    setPhoneCountry(region?.name?.toLowerCase());
  };

  if (!data?._id) {
    return loading ? <LoadingOverlay label="Loading" /> : null;
  }

  const tacticalMissingCredentials =
    access.isTactical &&
    (!data.tactical_credentials?.tactical_3plnext_credentials ||
      !data.tactical_credentials?.tactical_legacy_credentials);
  const paperType = (
    <FormSelect
      control={control}
      name="label_paper_type"
      label="Label Paper Type"
      hint="Choose warehouse paper preference; this is in order for all future shipments use the selected paper type at the warehouse (for box labels, pallet labels, and shipping labels)."
      options={PAPER_OPTIONS}
      placeholder="Select paper type"
      isDisabled={isUser || access.quantityAndPaper}
    />
  );
  const days = (label: string) => `${label} (days)`;

  return (
    <>
      {deleting ? <LoadingOverlay label="Deleting" fullScreen /> : null}
      <form
        autoComplete="off"
        onSubmit={handleSubmit(save)}
        className="sk:grid sk:grid-cols-2 sk:gap-4"
      >
        <Card
          className="sk:col-span-2"
          title="Business Details"
          action={
            access.autoUpload ? (
              <FormCheckbox
                control={control}
                name="auto_upload"
                label="Auto-Upload Tracking Label"
                hint={StorageProviderHelpText.autoUpload}
                isDisabled={isUser}
              />
            ) : null
          }
        >
          <div className="sk:flex sk:flex-col sk:gap-3">
            <FieldGrid columns={4}>
              <FormTextField
                control={control}
                name="name"
                label="Name"
                isRequired
                isDisabled={isUser || access.name}
              />
              <KindField
                control={control}
                name="is_fulfillable"
                label="Fulfillment Center Type"
                hint={StorageProviderHelpText.typeTooltip}
                title="Fulfills Individual Orders"
                detail="(Stores and Ships Individual Items)"
                isDisabled={isUser || access.fulfillment}
              />
              <KindField
                control={control}
                name="is_warehouse"
                label="Warehouse Type"
                hint={StorageProviderHelpText.warehouseTypeTooltip}
                title="Offers Storage and Forwarding"
                detail="(Stores Parcels / Pallets)"
                isDisabled={isUser || access.warehouse}
              />
              {paperType}
            </FieldGrid>
            {showKindError ? (
              <span className="sk:text-xs sk:text-danger">
                Please select at least one checkbox
              </span>
            ) : null}
            {access.quantityType || tacticalMissingCredentials ? (
              <div className="sk:flex sk:items-end sk:justify-between sk:gap-3">
                {access.quantityType ? (
                  <QuantityTypeField
                    control={control}
                    isDisabled={isUser || access.quantityAndPaper}
                  />
                ) : (
                  <span />
                )}
                {tacticalMissingCredentials ? (
                  <Button
                    variant="danger"
                    onPress={() => setCredentialsOpen(true)}
                  >
                    Missing Credentials
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        </Card>

        <Card
          className="sk:col-span-2"
          title="Shipment & Stock"
          action={
            data.restock_limit_url ? (
              <a
                href={data.restock_limit_url}
                target="_blank"
                rel="noreferrer"
                className={buttonStyles({ variant: 'ghost', size: 'sm' })}
              >
                Restock limits
                <ArrowSquareOutIcon size={14} aria-hidden />
              </a>
            ) : null
          }
        >
          <div className="sk:flex sk:flex-col sk:gap-3">
            <FieldGrid columns={4}>
              {isFulfillable ? (
                <FormNumberField
                  control={control}
                  name="holdback_qty"
                  label="Fulfillment Threshold Quantity"
                  hint={StorageProviderHelpText.holdbackQtyTooltip}
                  isDisabled={isUser || access.shipmentStock}
                />
              ) : null}
              {access.oosFulfillable ? (
                <KindField
                  control={control}
                  name="is_oos_fulfillable"
                  label="OOS Fulfillable"
                  hint="Whether out-of-stock items can still be fulfilled by this provider."
                  title="OOS Fulfillable"
                  detail="(Allow fulfillment for out-of-stock items)"
                  isDisabled={isUser || access.fulfillment}
                />
              ) : null}
              {isFulfillable ? (
                <FormNumberField
                  control={control}
                  name="handling_time"
                  label="Handling Time"
                  hint={StorageProviderHelpText.handlingTimeTooltip}
                  isDisabled={isUser || access.shipmentStock}
                />
              ) : null}
              <FormNumberField
                control={control}
                name="restock_limit"
                label="Restock Limit"
                hint={StorageProviderHelpText.restockTooltip}
                isDisabled={isUser || access.shipmentStock}
              />
              {isFBA ? paperType : null}
              {!isFBA && isFulfillable ? (
                <FormSelect
                  control={control}
                  name="order_cutoff_time"
                  label="Order CutOff Time"
                  hint={StorageProviderHelpText.orderCutOffTimeTooltip}
                  options={cutoffOptions}
                  placeholder="Select time"
                  isDisabled={isUser || access.shipmentStock}
                />
              ) : null}
              {isFBA ? (
                <FormNumberField
                  control={control}
                  name="inbound_buffer_days"
                  label={days('Inbound Buffer')}
                  hint={StorageProviderHelpText.inboundBufferDayTooltip}
                  isDisabled={isUser || access.shipmentStock}
                />
              ) : null}
              {isFulfillable ? null : (
                <FormNumberField
                  control={control}
                  name="outbound_buffer_days"
                  label={days('Outbound Buffer')}
                  hint={StorageProviderHelpText.outboundBufferDayTooltip}
                  isDisabled={isUser || access.shipmentStock}
                />
              )}
              {isWarehouse ? (
                <FormNumberField
                  control={control}
                  name="inventory_buffer_stock_days"
                  label={days('Minimum Desired Stock')}
                  hint="Select a minimum desired stock in days to carry for all items at a storage provider. This value is used for purchasing and shipment predictions."
                  isDisabled={isUser || access.shipmentStock}
                />
              ) : null}
            </FieldGrid>
            {isFBA ? (
              <FormCheckbox
                control={control}
                name="is_auto_accept_fba_charges"
                label="Auto Accept Amazon Charges"
              />
            ) : null}
          </div>
        </Card>

        {isFBA || isFulfillable ? (
          <Card className="sk:col-span-2" title="Minimum Desired Stock">
            <div className="sk:flex sk:flex-col sk:gap-3">
              <p className="sk:m-0 sk:text-dense sk:text-fg-secondary">
                Choose a minimum desired stock level per SKU, determined by SKU
                sales velocity.
              </p>
              <FieldGrid columns={3}>
                {STOCK_TIERS.map(({ tier, detail }) => (
                  <FormNumberField
                    key={tier}
                    control={control}
                    name={`inventory_buffer_days_${tier}`}
                    label={days(`Tier ${tier}`)}
                    hint={detail}
                    isDisabled={isUser}
                  />
                ))}
              </FieldGrid>
            </div>
          </Card>
        ) : null}

        {isFBA ? null : (
          <>
            <Card title="Location">
              <FieldGrid columns={2}>
                <FormTextField
                  control={control}
                  name="address_line_one"
                  label="Address 1"
                  isDisabled={isUser || access.detailsAndLocation}
                />
                <FormTextField
                  control={control}
                  name="address_line_two"
                  label="Address 2"
                  isDisabled={isUser || access.detailsAndLocation}
                />
                <FormTextField
                  control={control}
                  name="city"
                  label="City"
                  isDisabled={isUser || access.detailsAndLocation}
                />
                <FormTextField
                  control={control}
                  name="state"
                  label="State"
                  isDisabled={isUser || access.detailsAndLocation}
                />
                <FormTextField
                  control={control}
                  name="zip_code"
                  label="Zip Code"
                  error={
                    !zipValid && submitCount > 0
                      ? ERROR_MESSAGES.INVALID_ZIP_CODE
                      : undefined
                  }
                  isDisabled={isUser || access.detailsAndLocation}
                />
                <FormSelect
                  control={control}
                  name="country"
                  label="Country"
                  placeholder={regionLoading ? 'Loading…' : 'Select a country'}
                  options={countryOptions}
                  onSelect={onCountrySelect}
                  isDisabled={isUser || access.detailsAndLocation}
                />
                <FormTextField
                  control={control}
                  name="identifier"
                  label="Identifier/Tag"
                  isDisabled={isUser || access.detailsAndLocation}
                />
              </FieldGrid>
            </Card>

            <Card title="Contact Person">
              <FieldGrid columns={2}>
                <FormTextField
                  control={control}
                  name="contact_name"
                  label="Contact Name"
                  isDisabled={isUser || access.contact}
                />
                <FormPhoneField
                  control={control}
                  name="contact_phone"
                  label="Phone"
                  isDisabled={isUser || access.contact}
                  defaultCountry={phoneCountry}
                  onDialCodeChange={setDialCodeLength}
                />
                <FormTextField
                  control={control}
                  name="contact_email"
                  label="E-Mail"
                  isDisabled={isUser || access.contact}
                />
                <FormTextField
                  control={control}
                  name="contact_fax"
                  label="Fax"
                  isDisabled={isUser || access.contact}
                />
              </FieldGrid>
            </Card>
          </>
        )}

        <Card title="Label Settings">
          <div className="sk:flex sk:flex-col sk:gap-3">
            <ExcludedCarriersField
              control={control}
              scope="provider"
              className=""
              disabled={isUser || access.labelSettings || saving || loading}
            />
            <FieldGrid columns={2}>
              <FormTextField
                control={control}
                name="statement_descriptor"
                label="Statement Descriptor"
                hint={StorageProviderHelpText.statementDescriptor}
                isDisabled={isUser || access.labelSettings}
              />
              <SenderNameField
                control={control}
                isEnabled={useLabelName}
                isDisabled={isUser || access.labelSettings}
              />
            </FieldGrid>
          </div>
        </Card>

        <Card title="Amazon vendor warehouse codes">
          <WarehouseCodesField
            control={control}
            isDisabled={isUser || access.labelSettings}
          />
        </Card>

        <div className="sk:col-span-2 sk:flex sk:items-center sk:gap-2">
          {isManual ? (
            <Menu
              aria-label="Provider options"
              trigger={<Button>Options</Button>}
              actions={[
                { id: 'change-type', label: 'Change Storage Provider Type' },
              ]}
              onAction={() => setChangeTypeOpen(true)}
            />
          ) : null}
          {!isFBA && isAdmin ? (
            <Button variant="danger" onPress={() => setConfirmDelete(true)}>
              Delete {data.name}
            </Button>
          ) : null}
        </div>

        {isDirty && !loading && !deleting ? (
          <SaveBar
            onDiscard={() => reset(defaults)}
            saveType="submit"
            isDisabled={isUser}
            isSaving={saving}
          />
        ) : null}
      </form>

      <ConfirmModal
        isOpen={confirmDelete}
        title="Delete storage provider"
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
      >
        Are you sure you want to delete storage provider?
      </ConfirmModal>
      <AddProviderModal
        open={changeTypeOpen}
        closeModal={changed => {
          setChangeTypeOpen(false);
          if (changed) refresh();
        }}
        existingProvider={data}
      />
      <CredentialPopup
        open={credentialsOpen}
        onClose={() => setCredentialsOpen(false)}
        type="Tactical 3pl"
        providerData={data}
        refresh={refresh}
      />
    </>
  );
}
